/**
 * mapIncidentDetails
 *
 * Pure function that transforms raw API response data into the shape consumed
 * by useIncidentDetails (and from there by ModernIncidentDetailPage and its
 * child components).
 *
 * Extracting this from useIncidentDetails keeps the hook focused on fetching,
 * polling, and error handling, while this function owns the data shape.
 * New fields from future stories should be added here, not in the hook.
 *
 * @param {Object} params
 * @param {Object} params.problemData          - Top-level problem record from the API
 * @param {Object} params.outageData           - First outage extracted from problemData.outages
 * @param {string} params.validatedNumber      - Validated incident/PRB number
 * @param {string} params.firstOutageKey       - Key of the first outage (e.g. "OUTAGE 1")
 * @param {Array}  params.timelineEvents       - Consolidated timeline events (from TimelineAPI)
 * @returns {Object} Data shape for setData()
 */
export function mapIncidentDetails({
  problemData,
  outageData,
  validatedNumber,
  firstOutageKey = '',
  timelineEvents = [],
}) {
  // ── Affected brand ────────────────────────────────────────────────────────
  const brandParts = [];

  if (outageData.affectedBrand) {
    const brandName = typeof outageData.affectedBrand === 'object'
      ? outageData.affectedBrand.name
      : outageData.affectedBrand;
    if (brandName) brandParts.push(brandName);
  }

  if (outageData.affectedBrandCapability) {
    const capabilities = Array.isArray(outageData.affectedBrandCapability)
      ? outageData.affectedBrandCapability
      : [outageData.affectedBrandCapability];

    capabilities.forEach(capability => {
      if (capability) {
        const capabilityName = typeof capability === 'object'
          ? capability.name || capability.label || capability.value
          : capability;
        if (capabilityName) brandParts.push(capabilityName);
      }
    });
  }

  const affectedBrand = brandParts.join(', ') || '';

  // ── Impacted workloads ────────────────────────────────────────────────────
  // Handle multiple field name variations from the backend
  const impactedWorkloads = outageData.impactedApplications
    || outageData.impacted_applications
    || outageData.impactedApps
    || outageData.impacted_apps
    || [];

  // ── Last updated timestamp ────────────────────────────────────────────────
  // reduce() instead of Math.max(...spread) avoids a RangeError on large audit
  // trails (V8 stack limit ~65K args). Number.isFinite guards against malformed
  // changedAt values producing NaN, which Math.max would silently propagate.
  const changeLog = problemData.auditTrail?.changeLog || [];
  const rawMax = changeLog.reduce((max, e) => {
    if (e.changedAt == null) return max; // skip null/undefined — new Date(null) = epoch (0), a valid finite number
    const t = new Date(e.changedAt).getTime();
    return Number.isFinite(t) && t > max ? t : max;
  }, -Infinity);
  const lastUpdated = Number.isFinite(rawMax) ? rawMax : null;

  // ── Final shape ───────────────────────────────────────────────────────────
  // The record's number is a PRB or an INC. INC-keyed records carry the INC here,
  // so the PRB cell shows it only when it's actually a PRB (PRB/PRBT); an INC → ''.
  const recordNumber = problemData.number || validatedNumber;
  const prbNumber = recordNumber && recordNumber.startsWith('PRB') ? recordNumber : '';

  // US1950081: surfaced so consumers can skip FETCHING impacts for a temporary
  // problem instead of reacting to the refusal — the backend refuses impact reads
  // on a temporary record with a 403 (handler_impact.go:372 tests
  // `problem.Temporary`). Scope note: this gates fetching only. The "Report
  // Impact" button is gated by `isResolved` alone, not by this flag.
  //
  // READ THE FLAG, NOT THE NUMBER, AND DO NOT DERIVE THIS FROM `prbNumber` ABOVE.
  // `problem.Temporary` is the exact field the backend refuses on, so reading it
  // here cannot drift from the backend's rule. A number-prefix test would be a
  // second, independent definition of "temporary" that nothing keeps in sync.
  const temporary = problemData.temporary || false;

  return {
    currentStatus: outageData.status || 'Unknown',
    statusDescription: outageData.short_description || '',
    statusFullDescription: outageData.description || '',
    affectedBrand,
    impactedWorkloads,
    timeline: outageData.notes || [],
    timelineEvents,
    updatedAt: outageData.updated_at || outageData.updatedAt,
    updatedBy: outageData.updated_by || outageData.updatedBy,
    // Header fields
    number: recordNumber,
    shortDescription: outageData.short_description || '',
    priority: outageData.priority || 'EP4',
    incidentStart: outageData.incidentStart || null,
    // US2072496: the detail header's "Resolved" label needs the resolve time.
    // This was previously unmapped, so the header had no choice but to render
    // incidentStart under a "Resolved" label.
    incidentEnd: outageData.incidentEnd || null,
    nextUpdate: outageData.nextUpdate || null,
    clientImpact: outageData.impact || '',
    pagerDutyID: outageData.pagerDutyID || '',
    // Incident Details section fields (US1889423)
    slackChannel: outageData.slackChannel || '',
    incidentCommander: outageData.incidentCommander || '',
    majorIncidentManager: outageData.mim || '',
    incidentNumber: problemData.incidentNumber || '',
    awscase: outageData.awscase || '',
    // US2031592 [B5]: gated to the PRB family so INC-only records omit the PRB
    // cell (see the recordNumber/prbNumber derivation above). Merged with the
    // US2031587 [A3] awscase field.
    prbNumber,
    temporary,
    archived: problemData.archived || false,
    lastUpdated,
    outageKey: firstOutageKey,
  };
}
